/** What the four Processing tabs share: the frame, the three document tiles, the Upload action and
 * the file cell. Each tab is a view of the same journey (a file that was sent, read, and is waiting
 * for a person), so they share a header and the same server counts. */

import { ListMagnifyingGlass } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useMe } from "../../api/auth";
import type { DocumentRow } from "../../api/evidence";
import { stateCount } from "../../api/processing";
import { ReadFailed } from "../../components/Loaded";
import type { DocumentCounts } from "../../api/processing";
import { AddImportActions, DashboardBand, TablePage, Tile, TileStrip } from "../../components/ui";
import { quantity } from "../../lib/format";
import { useDocumentCharts } from "../bands/DocumentCards";
import { ScreenFrame } from "../ScreenFrame";
import { keepSearch } from "../search";
import { useScreen } from "../useScreen";
import styles from "./Processing.module.css";

export const count = (value: number | undefined): string =>
  value === undefined ? "–" : quantity(String(value));

/** To review, Reading and Refused: the server's counts of the whole library, so a tab of a hundred
 * rows read in pages prints the number the list is cut from. A count that has not been read is a
 * dash and never a zero. */
export function DocumentTiles({
  counts,
  pending = false,
  failure,
}: {
  counts: DocumentCounts | undefined;
  /** The counts have not answered yet: a grey bar stands where each number will be, never a dash. */
  pending?: boolean;
  /** The counts read, when it failed: the tiles give way to the shared failure with its retry, because
   * a strip of dashes beside a table that loaded reads as numbers nobody has. */
  failure?: { error: unknown; retry: () => void };
}) {
  if (failure?.error) {
    return <ReadFailed of="The document counts" error={failure.error} onRetry={failure.retry} />;
  }
  return (
    <TileStrip label="Documents in processing">
      <Tile
        label="To review"
        value={count(stateCount(counts, "awaiting_review"))}
        loading={pending}
        testId="tile-review"
        infoKey="processing.to-review"
      />
      <Tile
        label="Not yet read"
        value={count(stateCount(counts, "received", "validated", "extracting"))}
        loading={pending}
        testId="tile-reading"
        infoKey="processing.in-progress"
      />
      <Tile
        label="Refused"
        value={count(stateCount(counts, "quarantined"))}
        loading={pending}
        testId="tile-refused"
        infoKey="processing.refused"
      />
    </TileStrip>
  );
}

export function ProcessingFrame({
  tab,
  tiles,
  extra,
  documentCharts = false,
  filters,
  table,
  state,
  nothingYet = false,
}: {
  /** The tab's name, which is the label beside the title. */
  tab: string;
  /** Nothing has been uploaded: no chart and no Charts toggle (`TablePage`'s `nothingYet`). */
  nothingYet?: boolean;
  tiles: ReactNode;
  /** Between the tiles and the filter line: a band that renders nothing unless it has something to say
   * (Missing: a stopped feed). */
  extra?: ReactNode;
  /** Draw the documents' stages and arrivals above the table (the document tabs; not Missing, whose
   * subject is the census). */
  documentCharts?: boolean;
  filters?: ReactNode;
  table?: ReactNode;
  state?: ReactNode;
}) {
  const screen = useScreen();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const charts = useDocumentCharts();
  return (
    <ScreenFrame screen={screen} title="Processing">
      <TablePage
        icon={ListMagnifyingGlass}
        title="Processing"
        label={tab}
        actions={
          <AddImportActions
            role={me?.role}
            addLabel="Upload"
            onAdd={() => void navigate({ to: "/upload", search: keepSearch })}
          />
        }
        band={
          documentCharts ? <DashboardBand label="Processing dashboard" tiles={tiles} charts={charts} /> : undefined
        }
        tiles={documentCharts ? undefined : tiles}
        nothingYet={nothingYet}
        extra={extra}
        filters={filters}
        table={table}
        state={state}
      />
    </ScreenFrame>
  );
}

/** The file's name over its size, the first cell of every document row. */
export function FileCell({ document }: { document: DocumentRow }) {
  return (
    <span className={styles.file}>
      <span className={styles.name} title={document.original_filename ?? undefined}>
        {document.original_filename ?? "Untitled document"}
      </span>
    </span>
  );
}
